import Cookies from "js-cookie";
import { BASE_URL } from "../connections/connectionUrl";

// Pharmacy stock report export.
// Primary path: ONE call to /pharmacy/medicines/stock_report (backend
// aggregates batches, taxes, stock values and totals — no N+1 requests).
// Fallback: until that endpoint is deployed, page through the medicines
// list + per-medicine batches like before.
// Dates are emitted Excel-friendly: DD-MM-YYYY and DD-MM-YYYY HH:MM.

const authToken = () => {
  const auth = Cookies.get("authStaging");
  return auth ? JSON.parse(auth)?.token || "" : "";
};

const apiGet = async (path) => {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Token ${authToken()}`,
    },
  });
  if (!res.ok) throw new Error(`${path} -> HTTP ${res.status}`);
  return res.json();
};

// ---- date helpers (Excel-friendly, Indian locale) ----

// "2027-04-14" -> "14-04-2027"
const fmtDate = (s) => {
  if (!s) return s;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : s;
};

// "2026-07-30 05:06 PM" -> "30-07-2026 17:06"
const fmtDateTime = (s) => {
  if (!s) return s;
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!m) return fmtDate(s);
  let h = parseInt(m[4], 10) % 12;
  if (/pm/i.test(m[6])) h += 12;
  return `${m[3]}-${m[2]}-${m[1]} ${String(h).padStart(2, "0")}:${m[5]}`;
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const csvCell = (v) => {
  if (v === null || v === undefined) return "";
  let s = typeof v === "object" ? JSON.stringify(v) : String(v);
  if (/[",\n]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
};

export const HEADERS = [
  "Id", "Name", "Form type", "Dose", "Frequency", "Duration",
  "Is deleted", "Company", "Amount (MRP)", "Discount", "Purchase cost",
  "GST %", "CGST %", "SGST %", "Total stock left", "Stock value (at cost)",
  "Stock value (at MRP)", "Expiry date", "Flat price", "Hsn code",
  "Hsn name", "Batch numbers", "Created at", "Updated at",
];

const totalsRow = (stock, valueCost, valueMrp) => {
  const totals = new Array(HEADERS.length).fill("");
  totals[1] = "TOTAL";
  totals[14] = stock;
  totals[15] = round2(valueCost);
  totals[16] = round2(valueMrp);
  return totals;
};

// ---- primary path: single backend call ----

const rowsFromReport = (payload) => {
  const rows = (payload.medicines || []).map((m) => [
    m.id, m.name, m.form_type, m.dose, m.frequency, m.duration,
    m.is_deleted, m.company, m.amount, m.discount, m.purchase_cost,
    m.gst, m.cgst, m.sgst, m.total_stock_left,
    m.stock_value_at_cost, m.stock_value_at_mrp,
    m.expiry_date, m.flat_price, m.hsn_code, m.hsn_name,
    (m.batches || []).map((b) => b.batch_no).filter(Boolean).join(", "),
    m.created_at, m.updated_at,
  ]);
  const t = payload.totals || {};
  rows.push(totalsRow(
    t.total_stock_left || 0,
    t.stock_value_at_cost || 0,
    t.stock_value_at_mrp || 0,
  ));
  return rows;
};

// ---- fallback path: paged list + per-medicine batches ----

const fetchAllMedicines = async (onProgress) => {
  const perPage = 100;
  let page = 1;
  let all = [];
  let total = Infinity;
  while (all.length < total) {
    const data = await apiGet(
      `/api/v1/pharmacy/medicines?page=${page}&per_page=${perPage}`,
    );
    const meds = data?.payload?.medicines || [];
    total = Number(data?.meta?.total_count ?? meds.length);
    all = all.concat(meds);
    onProgress?.(`Fetching medicines ${Math.min(all.length, total)}/${total}`);
    if (meds.length === 0) break;
    page += 1;
  }
  return all;
};

const fetchDetails = async (medicines, onProgress) => {
  const results = new Array(medicines.length);
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < medicines.length) {
      const i = next++;
      try {
        const data = await apiGet(`/api/v1/pharmacy/medicines/${medicines[i].id}`);
        results[i] = data?.payload?.medicine || data?.payload || {};
      } catch {
        results[i] = {};
      }
      done += 1;
      if (done % 10 === 0 || done === medicines.length) {
        onProgress?.(`Fetching batches ${done}/${medicines.length}`);
      }
    }
  };
  await Promise.all(Array.from({ length: 8 }, worker));
  return results;
};

const avgPurchaseCost = (batches) => {
  const stocked = batches.filter(
    (b) => b.purchase_cost != null && (b.stock_left || 0) > 0,
  );
  if (stocked.length > 0) {
    const totalStock = stocked.reduce((s, b) => s + b.stock_left, 0);
    return round2(
      stocked.reduce((s, b) => s + b.purchase_cost * b.stock_left, 0) / totalStock,
    );
  }
  const priced = batches.filter((b) => b.purchase_cost != null);
  if (priced.length === 0) return null;
  return round2(priced.reduce((s, b) => s + b.purchase_cost, 0) / priced.length);
};

const legacyRows = async (onProgress) => {
  const medicines = await fetchAllMedicines(onProgress);
  const details = await fetchDetails(medicines, onProgress);

  const rows = [];
  let totalStock = 0;
  let totalValueCost = 0;
  let totalValueMrp = 0;

  medicines.forEach((m, i) => {
    const batches = details[i]?.medicine_batches || [];
    const activeBatch =
      batches.find((b) => b.batch_in_use) || batches[batches.length - 1] || {};

    const stockLeft =
      m.total_stock_left ??
      batches.reduce((s, b) => s + (b.stock_left || 0), 0);

    const valueCost = round2(
      batches.reduce(
        (s, b) => s + (b.stock_left || 0) * (b.purchase_cost || 0),
        0,
      ),
    );
    const valueMrp = round2(
      batches.reduce(
        (s, b) => s + (b.stock_left || 0) * (b.amount ?? m.amount ?? 0),
        0,
      ),
    );

    totalStock += stockLeft || 0;
    totalValueCost += valueCost;
    totalValueMrp += valueMrp;

    rows.push([
      m.id, m.name, m.form_type, m.dose, m.frequency, m.duration,
      m.is_deleted, m.company,
      m.amount ?? activeBatch.amount, m.discount,
      avgPurchaseCost(batches),
      activeBatch.gst, activeBatch.cgst, activeBatch.sgst,
      stockLeft, valueCost, valueMrp,
      fmtDate(m.expiry_date), m.flat_price, m.hsn_code, m.hsn_name,
      batches.map((b) => b.batch_no).filter(Boolean).join(", "),
      fmtDateTime(m.created_at), fmtDateTime(m.updated_at),
    ]);
  });

  rows.push(totalsRow(totalStock, totalValueCost, totalValueMrp));
  return rows;
};

// ---- entry point ----

export const exportStockReport = async (onProgress) => {
  onProgress?.("Preparing report…");

  let rows;
  try {
    const data = await apiGet("/api/v1/pharmacy/medicines/stock_report");
    rows = rowsFromReport(data?.payload || {});
  } catch (e) {
    // stock_report endpoint not deployed yet — fall back to per-medicine calls
    console.warn("[stockReport] single-call endpoint unavailable, using fallback:", e.message);
    rows = await legacyRows(onProgress);
  }

  const csv = [HEADERS, ...rows]
    .map((r) => r.map(csvCell).join(","))
    .join("\n");

  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  const today = new Date().toISOString().slice(0, 10);
  link.download = `medicine_stock_report_${today}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
  onProgress?.(null);
  return rows.length - 1; // excluding totals row
};
